import React, { useContext, useMemo } from 'react';
import classes from './CaptionWriter.module.scss';
import { Network } from '../../../models/graphql';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { IconsEnum } from '../../../constants/icons';
import { Button } from '../../../components/actions/Button';
import { CaptionWritterViewProps } from './CaptionWriter.view';
import { UseFormReturn, useWatch } from 'react-hook-form';
import classNames from 'classnames';
import { GENERATING_CAPTIONS_PLACEHOLDER } from '../states/initial';
import { Tooltip } from '../../../components/actions/Tooltip';
import { idGenerator } from '../../../helpers/idGenerator';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { CaptionWriterViewType, PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { STOPPED_CAPTIONS_PLACEHOLDER } from '../states/effects/aiCaptionWritter.effect';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { Link } from 'react-router-dom';

export type CaptionWriterFooterViewProps = {
  networks: Network[];
  goBack: CaptionWritterViewProps['goBack'];
  goToResults: CaptionWritterViewProps['goToResults'];
  submitHandler: CaptionWritterViewProps['submitHandler'];
  copyText: CaptionWritterViewProps['copyText'];
  aiFormInstance: UseFormReturn;
  viewType: CaptionWriterViewType;
};
export const CaptionWriterFooterView = (props: CaptionWriterFooterViewProps): JSX.Element => {
  const [postGroupData, dispatchPostGroupData] = useContext(PostEditorGroupDataContext);
  const { aiCaptionWriter } = postGroupData;
  const { captionResults, cacheKey, loading, limitReached } = aiCaptionWriter;
  const { canUseAiCaptions, isOwner } = useOrganizationPlan();
  const { networks, goBack, submitHandler, aiFormInstance, copyText, viewType, goToResults } = props;
  const hasProfiles = !!networks.length;
  let rightSide: JSX.Element;
  let leftSide: JSX.Element;

  const hasPromptContent = !!useWatch({
    control: aiFormInstance.control,
    name: 'prompt'
  });

  const id = useMemo(() => idGenerator(), []);
  const breakpoint = useBreakpoints();
  const isMobile = breakpoint === 'small';
  const shouldHideRegenerateLabel = breakpoint === 'tabletportrait';
  const canUseCaption = canUseAiCaptions(limitReached);

  switch (viewType) {
    case CaptionWriterViewType.initial: {
      // Left side
      if ([GENERATING_CAPTIONS_PLACEHOLDER].includes(captionResults)) {
        leftSide = <></>;
      } else {
        leftSide = (
          <Button
            type={'button'}
            variant={'hollow'}
            size={'medium'}
            color={ButtonColorsEnum.primary}
            action={goToResults}>
            Results
          </Button>
        );
      }
      // Right side
      if (!cacheKey) {
        rightSide = (
          <div className={'cell shrink'} id={`generateTooltip${id}`}>
            <Button
              type={'button'}
              action={submitHandler}
              variant={'solid'}
              size={'medium'}
              color={ButtonColorsEnum.primary}
              icon={{ side: 'left', value: IconsEnum.aiAssistant }}
              disabled={!hasProfiles || !hasPromptContent || limitReached || !canUseCaption}>
              Generate
            </Button>
            {hasProfiles && !hasPromptContent && canUseCaption ? (
              <Tooltip
                type={'regular'}
                target={`#generateTooltip${id}`}
                autoZIndex={false}
                style={{ zIndex: scssVars['z-index-overmodal'] }}>
                Type a prompt to get started
              </Tooltip>
            ) : (
              <></>
            )}
            {!canUseCaption && (
              <Tooltip
                type={'regular'}
                target={`#generateTooltip${id}`}
                autoZIndex={false}
                autoHide={false}
                style={{ zIndex: scssVars['z-index-overmodal'] }}>
                {isOwner ? (
                  <span>
                    You have run out of AI Text credits for this month. Please{' '}
                    <Link to={'/settings/manage-subscription?src=plan_limit_reached&limit=ai_text'} target="_blank">
                      choose a higher plan, or customize your current one with add-ons.
                    </Link>
                  </span>
                ) : (
                  <span>
                    You have run out of AI Text credits for this month. Please contact your Organization Owner to
                    upgrade your plan.
                  </span>
                )}
              </Tooltip>
            )}
          </div>
        );
      } else if (!loading) {
        rightSide = (
          <div className={'cell shrink'} id={`reGenerateTooltipSolid${id}`}>
            <Button
              type={'button'}
              action={submitHandler}
              variant={'solid'}
              size={'medium'}
              color={ButtonColorsEnum.primary}
              icon={
                isMobile
                  ? undefined
                  : { side: shouldHideRegenerateLabel ? 'center' : 'left', value: IconsEnum.reGenerate }
              }>
              {shouldHideRegenerateLabel ? undefined : 'Re-Generate'}
            </Button>
            {!hasProfiles && canUseCaption ? (
              <Tooltip
                type={'regular'}
                target={`#reGenerateTooltipSolid${id}`}
                autoZIndex={false}
                style={{ zIndex: scssVars['z-index-overmodal'] }}>
                Please select a profile to generate AI captions
              </Tooltip>
            ) : (
              <></>
            )}
            {!canUseCaption && (
              <Tooltip
                type={'regular'}
                target={`#reGenerateTooltipSolid${id}`}
                autoZIndex={false}
                autoHide={false}
                style={{ zIndex: scssVars['z-index-overmodal'] }}>
                {isOwner ? (
                  <span>
                    You have run out of AI Text credits for this month. Please{' '}
                    <Link to={'/settings/manage-subscription?src=plan_limit_reached&limit=ai_text'} target="_blank">
                      choose a higher plan, or customize your current one with add-ons.
                    </Link>
                  </span>
                ) : (
                  <span>
                    You have run out of AI Text credits for this month. Please contact your Organization Owner to
                    upgrade your plan.
                  </span>
                )}
              </Tooltip>
            )}
          </div>
        );
      } else {
        rightSide = <></>;
      }
      break;
    }
    case CaptionWriterViewType.results: {
      // left side
      if (!loading) {
        leftSide = (
          <Button
            type={'button'}
            variant={'hollow'}
            size={'medium'}
            color={ButtonColorsEnum.primary}
            icon={isMobile ? undefined : { side: 'left', value: IconsEnum.arrowLeft }}
            action={() => goBack()}>
            Back
          </Button>
        );
      } else {
        leftSide = <></>;
      }
      // right side
      if (loading) {
        rightSide = (
          <div className={'cell shrink'}>
            <Button
              type={'button'}
              variant={'solid'}
              size={'medium'}
              color={ButtonColorsEnum.primary}
              icon={{ side: 'left', value: IconsEnum.pause }}
              disabled={!cacheKey}
              action={() => dispatchPostGroupData({ type: 'stopAiCaptions' })}>
              Stop
            </Button>
          </div>
        );
      } else {
        rightSide = (
          <div className={'cell shrink'}>
            <div className={'grid-x align-right'}>
              <div
                className={classNames('cell', 'shrink', classes.CaptionsWriterFooterButtonWithMargins)}
                id={`reGenerateTooltip${id}`}>
                <Button
                  type={'button'}
                  action={submitHandler}
                  variant={'hollow'}
                  size={'medium'}
                  color={ButtonColorsEnum.primary}
                  disabled={!hasProfiles || !canUseCaption}
                  icon={
                    isMobile
                      ? undefined
                      : {
                          side: shouldHideRegenerateLabel ? 'center' : 'left',
                          value: IconsEnum.reGenerate
                        }
                  }>
                  {shouldHideRegenerateLabel ? undefined : 'Re-Generate'}
                </Button>
                {!hasProfiles && canUseCaption ? (
                  <Tooltip
                    type={'regular'}
                    target={`#reGenerateTooltip${id}`}
                    autoZIndex={false}
                    style={{ zIndex: scssVars['z-index-overmodal'] }}>
                    Please select a profile to generate AI captions
                  </Tooltip>
                ) : (
                  <></>
                )}
                {!canUseCaption && (
                  <Tooltip
                    type={'regular'}
                    target={`#reGenerateTooltip${id}`}
                    autoZIndex={false}
                    autoHide={false}
                    style={{ zIndex: scssVars['z-index-overmodal'] }}>
                    {isOwner ? (
                      <span>
                        You have run out of AI Text credits for this month. Please{' '}
                        <Link to={'/settings/manage-subscription?src=plan_limit_reached&limit=ai_text'} target="_blank">
                          choose a higher plan, or customize your current one with add-ons.
                        </Link>
                      </span>
                    ) : (
                      <span>
                        You have run out of AI Text credits for this month. Please contact your Organization Owner to
                        upgrade your plan.
                      </span>
                    )}
                  </Tooltip>
                )}
              </div>
              <div className={'cell shrink'}>
                <Button
                  type={'button'}
                  variant={'solid'}
                  size={'medium'}
                  action={copyText}
                  color={ButtonColorsEnum.primary}
                  disabled={
                    !captionResults ||
                    [GENERATING_CAPTIONS_PLACEHOLDER, STOPPED_CAPTIONS_PLACEHOLDER].includes(captionResults)
                  }
                  icon={isMobile ? undefined : { side: 'left', value: IconsEnum.clipboard }}>
                  Copy
                </Button>
              </div>
            </div>
          </div>
        );
      }
      break;
    }
  }

  return (
    <div className={'grid-x align-justify'}>
      <div className={'cell shrink'}>{leftSide}</div>
      {rightSide}
    </div>
  );
};
